import {
  Contact,
  EntityTypes,
  Property,
  PropertyGroup,
  Rule,
  Ruleset,
  User,
} from "types";
import {
  ReactNode,
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  formatLabel,
  getEntityTypeFromPath,
  getRequestHeaders,
  handleRes,
  logError,
} from "utils";
import { capitalize, debounce } from "@mui/material/utils";

import { AppContext } from "context/appContext";
import Autocomplete from "@mui/material/Autocomplete";
import { Stack } from "@mui/material";
import { SxProps } from "@mui/system";
import TextField from "@mui/material/TextField";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { AUTURA_ASPHALT, AUTURA_ASPHALT_LIGHT } from "context/themeContext";

type SearchableEntity =
  | Contact
  | User
  | Property
  | PropertyGroup
  | Rule
  | Ruleset;

export interface EntityLookupProps<T extends SearchableEntity> {
  onSelect: (item: T | null) => void;
  renderOption: (option: any) => ReactNode;
  entityType: EntityTypes;
  placeholder?: string;
  variant?: "standard" | "outlined" | "filled";
  margin?: "none" | "dense" | "normal";
  size?: "small" | "medium";
  disabled?: boolean;
  sx?: SxProps;
}

const EntityLookup = <T extends SearchableEntity>({
  onSelect,
  renderOption,
  entityType,
  variant = "outlined",
  disabled = false,
  sx,
  size = "small",
  margin = "none",
  placeholder,
}: EntityLookupProps<T>): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { pathname } = useLocation();

  // on tab change, clear the options
  useEffect(() => {
    setOptions([]);
    fetchSuggesitons("");
  }, [entityType]);

  const entityPath = useMemo(() => {
    switch (entityType) {
      case EntityTypes.Contact:
        return "contacts";
      case EntityTypes.User:
        return "users";
      case EntityTypes.Property:
        return "properties";
      case EntityTypes.PropertyGroup:
        return "propertyGroups";
      case EntityTypes.Rule:
        return "rules";
      case EntityTypes.Ruleset:
        return "rulesets";
      default:
        return "";
    }
  }, [entityType]);

  const baseUrl = useMemo(() => {
    switch (entityType) {
      case EntityTypes.Contact:
      case EntityTypes.User:
        return window.USER_BASE_URL;
      case EntityTypes.Property:
      case EntityTypes.PropertyGroup:
      case EntityTypes.Rule:
      case EntityTypes.Ruleset:
        return window.PROPERTY_BASE_URL;
      default:
        return "";
    }
  }, [entityType]);

  useEffect(() => {
    debounce(async () => {
      // if (inputValue) {
      fetchSuggesitons(inputValue);
      // }
    }, 300)();
  }, [inputValue]);

  const fetchSuggesitons = async (inputValue: string) => {
    if (inputValue.length < 3) {
      return;
    }
    setLoading(true);
    try {
      const data = await fetch(
        `${baseUrl}/tenant/${state.tenant.id}/${entityPath}?name=${inputValue}`,
        {
          method: "GET",
          ...getRequestHeaders(),
        },
      ).then((res) => handleRes<T[]>(res, 200));
      if (data.length > 1) {
        setOptions(
          data.slice(0, 1).sort((entityA, entityB) => {
            try {
              if (EntityTypes.User && entityA.firstName && entityB.firstName) {
                return entityA.firstName.localeCompare(entityB.firstName);
              } else {
                return entityA.name.localeCompare(entityB.name);
              }
            } catch (e) {
              return 0;
            }
          }),
        );
      }
      setOptions(
        data.sort((entityA, entityB) => {
          try {
            if (EntityTypes.User && entityA.firstName && entityB.firstName) {
              return entityA.firstName.localeCompare(entityB.firstName);
            } else {
              return entityA.name.localeCompare(entityB.name);
            }
          } catch (e) {
            return 0;
          }
        }),
      );
    } catch (e) {
      logError(new Error(e as string));
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (e: SyntheticEvent, value: T | null) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(value);
    setInputValue("");
  };

  const handleOnInputChange = (e: SyntheticEvent, value: string) => {
    setInputValue(value);
  };

  const placeholderText = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }
    return `${capitalize(t("add"))} ${capitalize(entityPath)} to ${formatLabel(
      getEntityTypeFromPath(pathname),
    )}`;
  }, [placeholder, pathname, entityPath]);

  // MUI didn't type the props for renderOption so we have to use any
  const handleRenderOption = (props: any, option: T) => {
    return (
      <Stack
        direction={"row"}
        spacing={1}
        key={`${option.id}`}
        p={2}
        onClick={() => onSelect(option)}
        sx={{
          cursor: "default",
          "&:hover": {
            backgroundColor: "whitesmoke",
          },
        }}
      >
        {renderOption(option)}
      </Stack>
    );
  };

  return (
    <Autocomplete
      sx={{
        borderRadius: 1,
        border: `1px solid rgb(114,114,114)`,
        m: 0,
        backgroundColor: "white",
        height: 40,
        flex: 1,
        ...sx,
      }}
      size={size}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        setInputValue("");
      }}
      options={options}
      value={null}
      disabled={disabled}
      blurOnSelect
      aria-label={capitalize(`${entityType} ${t("lookup")}`)}
      filterSelectedOptions
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id;
      }}
      getOptionKey={(option) => `${option.id}`}
      getOptionLabel={(option) => `${option.id}`}
      noOptionsText={t("noOptions", { name: t("items") })}
      loading={loading}
      loadingText={t("searching")}
      filterOptions={(x) => x}
      onInputChange={handleOnInputChange}
      onChange={handleOnChange}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholderText}
          variant={variant}
          margin={margin}
          fullWidth
          InputLabelProps={{ shrink: false }}
          InputProps={{
            ...params.InputProps,
            sx: { px: 2, borderRadius: 1 },
          }}
        />
      )}
      renderOption={handleRenderOption}
    />
  );
};

export default EntityLookup;
